import IAuthProvider from './IAuthProvider';
import createAuth0Client, { Auth0Client } from '@auth0/auth0-spa-js';
import Auth0Config from './auth0config';
import TokenResult from './tokenResult';

export class Auth0Provider implements IAuthProvider {
  private logoutRedirectUrl: string;
  private scope: string;
  private client: Auth0Client | null = null;
  private clientInitialization: Promise<void>;

  constructor(authConfig: Auth0Config) {
    this.logoutRedirectUrl = authConfig.logoutRedirectUrl;
    this.scope = authConfig.scope;

    // Initialize Auth0Client asynchronously
    this.clientInitialization = this.initializeAuth0Client(authConfig);
  }

  private async getClient(): Promise<Auth0Client> {
    await this.clientInitialization;
    if (!this.client) {
      throw new Error('Auth0Client not initialized');
    }

    return this.client;
  }

  private async initializeAuth0Client(authConfig: Auth0Config): Promise<void> {
    try {
      this.client = await createAuth0Client({
        domain: authConfig.domain,
        client_id: authConfig.clientId,
        client_secret: undefined, // Not used in browser-based SPA
        audience: authConfig.audience,
        redirect_uri: authConfig.loginRedirectUri,
        response_type: authConfig.responseType,
        scope: authConfig.scope,
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        useFormData: true
      });
    } catch (error) {
      console.error('Failed to initialize Auth0Client:', error);
    }
  }

  async login(): Promise<void> {
    const client = await this.getClient();
    await client.loginWithRedirect({
      appState: { returnTo: window.location.pathname }
    });
  }

  async logout(): Promise<void> {
    const client = await this.getClient();
    client.logout({
      returnTo: this.logoutRedirectUrl
    });
  }

  async renewTokensAsync(): Promise<TokenResult> {
    console.log('Renewing tokens in the background...');
    try {
      const client = await this.getClient();
      const accessToken = await client.getTokenSilently();
      const claims = await client.getIdTokenClaims();

      if (accessToken) {
        const decoded = this.decodeToken(accessToken);
        const expiresIn = decoded.exp ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000) : 3600;

        return {
          accessToken: accessToken,
          idToken: claims ? claims.__raw : '',
          expiresIn: expiresIn,
          scope: this.scope,
          tokenType: 'Bearer'
        };
      }
      return null as any;
    } catch (error) {
      console.warn('Token renewal failed:', error);
      return null as any;
    }
  }

  async resetPasswordAsync(email: string): Promise<void> {
    try {
      const client = await this.getClient();
      await client.loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    } catch (error) {
      console.error('Password reset failed:', error);
      throw new Error('An error occurred while trying to reset the password.');
    }
  }

  async handleAuthenticationResult(callback: (result: TokenResult, error: any) => void): Promise<void> {
    try {
      const client = await this.getClient();

      // Ignore when we are not returning from Auth0 callback.
      const hasAuthParams =
        window.location.search.indexOf('code=') >= 0 ||
        window.location.search.indexOf('error=') >= 0 ||
        window.location.hash.indexOf('access_token=') >= 0;
      if (!hasAuthParams) {
        return;
      }

      await client.handleRedirectCallback();

      const isLoggedIn = await client.isAuthenticated();
      if (isLoggedIn) {
        const accessToken = await client.getTokenSilently();
        const claims = await client.getIdTokenClaims();

        const decoded = this.decodeToken(accessToken);
        const expiresIn = decoded.exp ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000) : 3600;

        callback(
          {
            accessToken: accessToken,
            idToken: claims ? claims.__raw : '',
            expiresIn: expiresIn,
            scope: this.scope,
            tokenType: 'Bearer'
          },
          null
        );
      } else {
        callback(null as any, new Error('Authentication did not complete successfully.'));
      }
    } catch (error) {
      console.error('Authentication handling failed:', error);
      callback(null as any, error);
    }
  }

  private decodeToken(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  }
}
